import { useMemo, useState } from 'react';
import css from './style.module.scss';
import useFetch from '../../hooks/useFetch';
import fetchGql, { gql } from '../../util/fetchGql';
import cls from '../../util/cls';
import mergeEvents from '../../util/mergeEvents';
import Button from '../Button';
import formDataToObj from '../../util/formDataToObj';
import Check from '../../svg/check.svg';

const typeToType = type => ({
	Email: 'email',
	Phone: 'tel',
	Date: 'date',
}[type] ?? 'text');

const Label = ({ label, children }) => (
	<label className={css.label}>
		<span>{label}</span>
		{children}
	</label>
);

export default function Form ({ handle, product }) {
	const [busy, setBusy] = useState(false)
		, [err, setErr] = useState(null)
		, [complete, setComplete] = useState(false);

	const { data, loading } = useFetch(gql`
		query Form ($handle: String!) {
			formieForm (handle: [$handle]) {
                settings {
	                submitActionMessageHtml
                }

                formFields {
                    uid
                    name
	                handle
                    displayName
	                placeholder
	                required
                    inputTypeName
	                
	                ... on Field_Hidden {
		                defaultValue
	                }
	                
	                ... on Field_Dropdown {
		                options {
			                label
			                value
		                }
	                }
                }
				
				pages {
					settings {
						submitButtonLabel
					}
				}
			}
		}
	`, { handle }, `form_${handle}`);

	const { pages, settings, formFields = [] } = data?.formieForm ?? {};

	const { submissionParams, submissionArgs } = useMemo(() => {
		const submissionParams = [];
		const submissionArgs = [];
	
		for (let i = 0, l = formFields.length; i < l; i++) {
		  const { handle, inputTypeName } = formFields[i];
		  submissionParams.push(`$${handle}: ${inputTypeName}`);
		  submissionArgs.push(`${handle}: $${handle}`);
		}
	
		return {
		  submissionParams: submissionParams.join(' '),
		  submissionArgs: submissionArgs.join(' '),
		};
	}, [formFields]);
	

	if (loading)
		return 'Loading...';

	if (!data?.formieForm)
		return null;

	const onSubmit = async e => {
		e.preventDefault();
		setBusy(true);
		setErr(null);
		const body = new FormData(e.target);
		const variables = formDataToObj(body);

		const [, errors] = await fetchGql(gql`
			mutation SubmitForm (${submissionParams}) {
				save_${handle}_Submission (${submissionArgs}) {
					__typename
				}
			}
		`, variables, null, true);

		setBusy(false);

		if (errors) {
			try {
				const errArray = JSON.parse(errors[0].message);
				setErr(errArray[Object.keys(errArray)[0]]);
			} catch (e) {
				setErr(errors[0].message);
			}
		} else {
			setComplete(true);
		}
	};

	const onTextAreaInput = e => {
		let { borderTopWidth, borderBottomWidth } = window.getComputedStyle(e.target);
		borderTopWidth = +borderTopWidth.replace(/[^\d.]/g, '');
		borderBottomWidth = +borderBottomWidth.replace(/[^\d.]/g, '');
		e.target.style.height = '';
		e.target.style.height = (e.target.scrollHeight + borderTopWidth + borderBottomWidth) + 'px';
	};

	if (complete)
		return (
			<div className={css.complete}>
				<div>
					<Check />
					<h3>Thank You!</h3>
					<div dangerouslySetInnerHTML={{__html:settings.submitActionMessageHtml}} />
				</div>
			</div>
		);

	return (
		<form key={`form_${handle}`} className={css.form} onSubmit={onSubmit}>
			{err && (
				<div className={css.error}>
					<strong>Error</strong><br/>
					{err}
				</div>
			)}

{formFields?.map(field => {
    switch (field.displayName) {
        case 'Hidden':
            return (
                <input
                    key={field.uid}
                    type="hidden"
                    name={field.handle}
                    defaultValue={field.handle === 'product' ? product : field.defaultValue}
                />
            );
        case 'Dropdown':
            return (
                <Label key={field.uid} label={field.name}>
                    <select
                        key={`field_${field.uid}`}
                        name={field.handle}
                        className={cls(css.input, css.select)}
                        placeholder={field.placeholder}
                        required={field.required}
                    >
                        {field.options.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </Label>
            );
        case 'MultiLineText':
            return (
                <Label key={field.uid} label={field.name}>
                    <textarea
                        key={`field_${field.uid}`}
                        name={field.handle}
                        className={cls(css.input, css.textarea)}
                        placeholder={field.placeholder}
                        required={field.required}
                        onInput={mergeEvents(onTextAreaInput)}
                        rows={7}
                    />
                </Label>
            );
        default:
            return (
                <Label key={field.uid} label={field.name}>
                    <input
                        key={`field_${field.uid}`}
                        type={typeToType(field.displayName)}
                        name={field.handle}
                        className={css.input}
                        placeholder={field.placeholder}
                        required={field.required}
                    />
                </Label>
            );
    }
})}


			<footer className={css.footer}>
				<Button wide type="submit" busy={busy}>
					{pages?.[0]?.settings?.submitButtonLabel}
				</Button>
			</footer>
		</form>
	);
}
